/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { Column, Line } from '@ant-design/plots';
import { message, DatePicker } from 'antd';
import moment from 'moment';

import {
  SectionCard,
  StatCard,
  ChartCard,
  FilterDropdown,
  DataTable,
} from '../../ui';
import { CHART_SEMANTIC, CHART_AXIS } from '../../ui/chartPalette';
import { useUrlFilters } from '../../utils/useUrlFilters';
import {
  queryGmvData,
  gmvTendencyChart,
  getSettlementData,
  boardOrderData,
} from '@/services/athenaBoard';
import { tradeOrderColumns } from './columns/tradeOrderColumns';
import styles from './board.less';

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

/** URL 里读回的值都是字符串：只有解析不出来时才回落默认值（`||` 会把合法的 0 误判掉） */
const toInt = (v, fallback) => {
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? fallback : n;
};

// 后端 dateRange=8 表示自定义时间区间
const DATE_RANGE_CUSTOM = 8;

/**
 * 交易经营
 * ------------------------------------------------------------------
 * GMV 数据与趋势 / 结算金额 三块沿用后台既有真实接口
 * （/athena/gmvData、/athena/gmvTendencyChart、/athena/getSettlementData）；
 * 订单数据来自新的 /athena/board/orderData，口径与 h5 的
 * TenantStoreApi/memberData?dataType=1 对齐：
 * 新订单 firstOrderNum、复购订单 repeatOrderNum、成交额 totalAmountName。
 *
 * 2026-09-17 展示层改造（方案 P2）—— **取数逻辑未动**：
 *   1. 明细表换 `DataTable`（吸顶 / 空态 / 数值排版统一），列定义外置到
 *      `columns/tradeOrderColumns.js`；
 *   2. 图表取色统一走 `ui/chartPalette`（原先本页自写一串蓝色/网格灰/坐标轴灰
 *      字面量，与其它图同义不同色）；
 *   3. 三处筛选写进 URL（维度 / 时间 / 订单类型），刷新、分享、返回都能还原；
 *   4. 订单请求补 `.catch`：原先只有 then，请求失败时 loading 会一直转。
 */
const TradeOps = (props) => {
  const { tenantId, coachUserId, enums = {} } = props;

  /* 筛选与 URL 同步（方案 §5.1）。key 带业务域前缀（trend* / order*），
     避免与其它页面的同名参数互相污染 —— 与 ServiceDelivery 的 course* 同约定。 */
  const { filters, setFilter, setAll } = useUrlFilters({
    trendDim: 'month',
    orderRange: 3,
    orderType: 0,
    orderStart: '',
    orderEnd: '',
  });
  const dimension = filters.trendDim || 'month';
  const dateRange = toInt(filters.orderRange, 3);
  const orderType = toInt(filters.orderType, 0);
  /* 只有自定义区间（dateRange=8）才带日期，避免把上一次选的日期透给后端 */
  const startDate =
    dateRange === DATE_RANGE_CUSTOM ? filters.orderStart || null : null;
  const endDate =
    dateRange === DATE_RANGE_CUSTOM ? filters.orderEnd || null : null;
  const hasCompleteCustomRange =
    dateRange !== DATE_RANGE_CUSTOM || Boolean(startDate && endDate);

  const [gmv, setGmv] = useState({});
  const [trend, setTrend] = useState([]);
  const [settlement, setSettlement] = useState([]);

  const [order, setOrder] = useState({});
  const [orderLoading, setOrderLoading] = useState(false);

  /* ---- 真实数据 ---- */
  useEffect(() => {
    if (!tenantId) return;
    queryGmvData({ requestData: { tenantId } }).then((res) => {
      if (res.code === '00000') setGmv(res.data || {});
      else message.error(res.msg);
    });
    getSettlementData({ requestData: { tenantId } }).then((res) => {
      if (res.code !== '00000') return;
      const list = res.data || [];
      setSettlement([
        ...list.map((i) => ({
          month: i.monthStr,
          value: num(i.settlementIncome),
          type: '结算收入',
        })),
        ...list.map((i) => ({
          month: i.monthStr,
          value: num(i.settlementExpenditure),
          type: '提现金额',
        })),
      ]);
    });
  }, [tenantId]);

  const loadTrend = useCallback(
    (dim) => {
      if (!tenantId) return;
      gmvTendencyChart({ requestData: { tenantId, dimension: dim } }).then(
        (res) => {
          if (res.code !== '00000') return;
          const list = res.data || [];
          setTrend([
            ...list.map((i) => ({
              month: i.date,
              value: num(i.additionalIncome),
              type: '新增收入',
            })),
            ...list.map((i) => ({
              month: i.date,
              value: num(i.renewalIncome),
              type: '续费收入',
            })),
          ]);
        },
      );
    },
    [tenantId],
  );

  useEffect(() => {
    loadTrend(dimension);
  }, [dimension, loadTrend]);

  /* ---- 订单数据 ----
     依赖刻意列成基本类型（而不是一个 filter 对象）：对象字面量每次渲染都是新引用，
     会把这个 effect 变成每次都跑。 */
  useEffect(() => {
    let active = true;
    // 同 WeekReport：coachUserId 未到齐不发请求，且列入依赖（BFF 对缺参显式报错）
    if (!tenantId || !coachUserId) return;
    // 自定义时间必须同时选中开始和结束时间，避免切换选项时提前请求
    if (!hasCompleteCustomRange) {
      setOrderLoading(false);
      return;
    }
    setOrderLoading(true);
    boardOrderData({
      tenantId,
      coachUserId,
      dateRange,
      orderType,
      startDate,
      endDate,
    })
      .then((res) => {
        if (!active) return;
        setOrderLoading(false);
        /* res 本身也可能是空（网关层异常），先判空再取字段 */
        if (res && res.code === '00000') setOrder(res.data || {});
        else message.error((res && res.msg) || '订单数据加载失败');
      })
      .catch(() => {
        if (!active) return;
        /* 原来只有 then：请求失败时 loading 永远转下去 */
        setOrderLoading(false);
        message.error('订单数据加载失败，请稍后重试');
      });
    return () => {
      active = false;
    };
    // startDate/endDate 是 filters 的派生值，必须保留在依赖中以响应日期补全
  }, [tenantId, dateRange, orderType, startDate, endDate, coachUserId]);

  /* 今天/昨天口径下只出明细（源不给按日聚合），与 H5 tab1Content.vue:60 同构 */
  const showOrderList = dateRange === 1 || dateRange === 2;

  return (
    <div className={styles.boardBody}>
      <SectionCard title="GMV 数据">
        <div className={styles.kpiRow}>
          <StatCard title="总 GMV" value={gmv.totalGmv} precision={2} />
          <StatCard
            title="累计月卡 / 年卡"
            value={`${gmv.totalLiteMonthOrderNum ?? '-'}/${
              gmv.totalLiteYearOrderNum ?? '-'
            }`}
          />
          <StatCard
            title="累计 SP 订单"
            value={gmv.totalSpOrderNum}
            unit="单"
          />
          <StatCard
            title="累计 PRO 订单"
            value={gmv.totalProOrderNum}
            unit="单"
          />
        </div>
      </SectionCard>

      <SectionCard
        title="收入趋势"
        subtitle={
          dimension === 'month' ? '按月 · 新增与续费' : '按日 · 新增与续费'
        }
        extra={
          <FilterDropdown
            label="维度"
            value={dimension}
            options={enums.gmvDimension || []}
            onChange={(v) => setFilter('trendDim', v)}
          />
        }
      >
        <ChartCard height={360} empty={!trend.length}>
          {dimension === 'month' ? (
            <Column
              height={360}
              data={trend}
              isStack
              xField="month"
              yField="value"
              seriesField="type"
              legend={{ position: 'top-left' }}
              xAxis={{ label: { autoHide: true, autoRotate: false } }}
            />
          ) : (
            <Line
              height={360}
              data={trend}
              xField="month"
              yField="value"
              seriesField="type"
              legend={{ position: 'top-left' }}
              xAxis={{ label: { autoHide: true } }}
            />
          )}
        </ChartCard>
      </SectionCard>

      <SectionCard title="结算金额" subtitle="按月 · 结算收入与提现金额">
        <ChartCard height={340} empty={!settlement.length}>
          <Column
            height={340}
            data={settlement}
            isGroup
            xField="month"
            yField="value"
            seriesField="type"
            legend={{ position: 'top-left' }}
          />
        </ChartCard>
      </SectionCard>

      <SectionCard
        title="订单数据"
        subtitle=""
        loading={orderLoading}
        extra={
          <div className={styles.filterBar}>
            <FilterDropdown
              label="时间"
              value={dateRange}
              options={enums.dateRange || []}
              onChange={(v) =>
                /* 离开自定义区间时把日期一并清掉，避免 URL 里留着一组用不到的日期 */
                v === DATE_RANGE_CUSTOM
                  ? setFilter('orderRange', v)
                  : setAll({ orderRange: v, orderStart: '', orderEnd: '' })
              }
            />
            {/* 画板要求「选择日期」；dateRange=8 时后端靠 startDate/endDate 取区间，
                不选满会直接被 BFF 拒绝（而不是 500） */}
            {dateRange === DATE_RANGE_CUSTOM && (
              <DatePicker.RangePicker
                allowClear={false}
                value={
                  startDate && endDate
                    ? [moment(startDate), moment(endDate)]
                    : undefined
                }
                onChange={(_v, strs) =>
                  setAll({
                    orderRange: 8,
                    orderStart: strs?.[0] || '',
                    orderEnd: strs?.[1] || '',
                  })
                }
              />
            )}
            <FilterDropdown
              label="订单类型"
              value={orderType}
              options={enums.orderType || []}
              onChange={(v) => setFilter('orderType', v)}
            />
          </div>
        }
      >
        <div className={`${styles.kpiRow} ${styles.kpiRow3}`}>
          <StatCard
            title="新订单"
            value={order.firstOrderNum}
            unit="单"
            hint="周期内首次成交"
          />
          <StatCard
            title="复购订单"
            value={order.repeatOrderNum}
            unit="单"
            hint="周期内二次及以上成交"
          />
          <StatCard title="成交额" value={order.totalAmountName} />
        </div>
        {showOrderList ? (
          <DataTable
            size="small"
            rowKey={(record, index) => `${record.userId || ''}-${index}`}
            columns={tradeOrderColumns}
            dataSource={order.memberList || []}
            emptyText="该时段暂无订单明细"
            emptyHint="可切换时间范围或订单类型再看"
            pagination={{ pageSize: 10, showSizeChanger: false }}
          />
        ) : (
          /* 商家端这里是平滑折线 + 圆点 + 虚线网格，不是柱子 */
          <ChartCard height={280} empty={!(order.tendency || []).length}>
            <Line
              height={280}
              smooth
              data={order.tendency || []}
              xField="date"
              yField="num"
              legend={false}
              color={CHART_SEMANTIC.primary}
              point={{
                size: 4,
                shape: 'circle',
                style: {
                  fill: CHART_SEMANTIC.surface,
                  stroke: CHART_SEMANTIC.primary,
                  lineWidth: 2,
                },
              }}
              lineStyle={{ lineWidth: 2 }}
              yAxis={{
                grid: {
                  line: {
                    style: { lineDash: [4, 4], stroke: CHART_AXIS.grid },
                  },
                },
              }}
              xAxis={{
                line: { style: { stroke: CHART_AXIS.axisLine } },
                tickLine: { style: { stroke: CHART_AXIS.axisLine } },
                label: { autoHide: true },
              }}
            />
          </ChartCard>
        )}
      </SectionCard>
    </div>
  );
};

export default TradeOps;
